import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type FC,
  type SetStateAction,
} from 'react';
import { difference, union } from 'lodash-es';
import { atom, useAtom } from 'jotai';
import {
  Checkbox,
  DismissibleTag,
  InlineLoading,
  InlineNotification,
  RadioButton,
  RadioButtonGroup,
  Tag,
} from '@carbon/react';
import type { ColumnFiltersState } from '@tanstack/react-table';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import { pagedCsvExport } from 'components/data-table/fetch-all-pages';
import type {
  DataTableSelectionState,
  ExportConfig,
  TableState,
} from 'components/data-table/types';
import { DocumentsSearch } from 'components/documents-search';
import { FormattedCount } from 'components/formatted-count';
import { VestingDocumentDetail } from 'components/related-items/vesting-document-detail';
import { EMPTY } from 'components/vesting-documents/types';
import { WorkspaceCardNote } from 'components/workspace-cards';
import {
  buildColumns,
  flattenResults,
  type CrmTractTableRow,
} from 'pages/crm-tracts-subtracts/crm-tracts-content';
import { CrmTractFilters } from 'pages/crm-tracts-subtracts/crm-tracts-filters';
import { STATIC_TABLE_CONFIG } from 'pages/crm-tracts-subtracts/tract-summary/utils';
import {
  createCrmTractSearchAtom,
  type CrmTractSearchParams,
} from 'pages/crm-tracts-subtracts/atoms';
import {
  CRM_TRACTS_DEFAULT_PARAMS,
  HAS_DOCUMENT_STATUSES,
  crmTractColumnFiltersToParams,
  crmTractPanelFilters,
  isFilteringToHasDocument,
} from 'pages/crm-tracts-subtracts/utils';
import type { DocumentResponse } from 'types/api-responses';
import { fetchCrmTracts, useOpenDrafts, useTractVestingDocuments } from './api';
import styles from './page.module.scss';
import { withTargetNpvColumn } from 'components/target-npv';
import { withPartyLinks } from './party-links';

// Held by the caller, so a wizard step that unmounts keeps its filters.
interface TractSearch {
  params: CrmTractSearchParams;
  columnFilters: ColumnFiltersState;
}
export const EMPTY_TRACT_SEARCH: TractSearch = {
  params: CRM_TRACTS_DEFAULT_PARAMS,
  columnFilters: [],
};

export const tractColumns = () =>
  withPartyLinks(
    withTargetNpvColumn(
      buildColumns(new Set(), () => undefined).filter(
        (column) => column.id !== 'expander'
      ) as DataTableColumnDef<CrmTractTableRow>[]
    )
  );

// A tract the workspace already holds says so beside its name.
const markExisting = (
  columns: DataTableColumnDef<CrmTractTableRow>[],
  existing: Set<string>
) =>
  columns.map((column): DataTableColumnDef<CrmTractTableRow> => {
    const { cell } = column;
    if (column.id !== 'name' || typeof cell !== 'function') return column;
    return {
      ...column,
      cell: (context) => (
        <>
          {cell(context)}
          {existing.has(context.row.original.id) && (
            <>
              {' '}
              <Tag type="gray" size="sm">
                Already in workspace
              </Tag>
            </>
          )}
        </>
      ),
    };
  });

// Each page the tract search serves also looks up its tracts' vesting documents and Target NPVs.
const EXPORT_PAGE_SIZE = 1000;
// The tract index's max_result_window: ES refuses a page that reaches past it.
const MAX_EXPORT_ROWS = 10_000;

const tractsExport = (
  params: CrmTractSearchParams,
  totalCount: number
): ExportConfig => {
  const config = pagedCsvExport(
    'tracts',
    (page, pageSize, signal) => {
      // The count can grow past the cap after the preset was enabled.
      if (page * pageSize > MAX_EXPORT_ROWS)
        throw new Error(`Cannot export more than ${MAX_EXPORT_ROWS} rows.`);
      // No timeout: an export asks for 1,000 tracts a page.
      return fetchCrmTracts({ ...params, page, pageSize }, signal, {
        timeout: false,
      });
    },
    EXPORT_PAGE_SIZE,
    (results) => flattenResults(results).filter((row) => !row.isSubtract)
  );
  if (totalCount <= MAX_EXPORT_ROWS) return config;
  return {
    exportPresets: config.exportPresets?.map((preset) => ({
      ...preset,
      disabledReason: 'Narrow the search to 10,000 tracts or fewer to export.',
    })),
  };
};

// Select-all toggles the loaded page's ids: server-side "every match" would shift with paging and search.
const nextIds = (
  ids: string[],
  next: DataTableSelectionState,
  visible: string[]
) => {
  if (!next.selectAll) return next.toggledNodes;
  return visible.every((id) => ids.includes(id))
    ? difference(ids, visible)
    : union(ids, visible);
};

const TractPicker: FC<{
  ids: string[];
  onIdsChange: (ids: string[]) => void;
  onRowsChange: (rows: CrmTractTableRow[]) => void;
  search: TractSearch;
  onSearchChange: Dispatch<SetStateAction<TractSearch>>;
  /** Tracts already in the workspace: listed, but not pickable. */
  existing?: Set<string>;
}> = ({ ids, onIdsChange, onRowsChange, search, onSearchChange, existing }) => {
  const { searchAtom } = useMemo(() => createCrmTractSearchAtom(), []);
  const [result, runSearch] = useAtom(searchAtom);
  const { params, columnFilters } = search;

  useEffect(() => {
    runSearch(() => params);
  }, [params, runSearch]);

  const rows = useMemo(
    () =>
      flattenResults(result.data?.results ?? []).filter(
        (row) => !row.isSubtract
      ) as CrmTractTableRow[],
    [result.data?.results]
  );
  useEffect(() => onRowsChange(rows), [rows, onRowsChange]);
  const pickable = useMemo(
    () => rows.map((row) => ({ ...row, _canSelect: !existing?.has(row.id) })),
    [rows, existing]
  );

  const columns = useMemo(
    () => (existing ? markExisting(tractColumns(), existing) : tractColumns()),
    [existing]
  );

  const update = (patch: Partial<CrmTractSearchParams>) =>
    onSearchChange((current) => ({
      ...current,
      params: { ...current.params, page: 1, ...patch },
    }));

  return (
    <DataTable<CrmTractTableRow>
      data={pickable}
      columns={columns}
      getRowId={(row) => row.id}
      isLoading={result.loading}
      error={result.error ?? null}
      config={{
        selectionType: 'multi',
        columnFilters: false,
        sizeToContent: true,
        export: tractsExport(params, result.data?.totalCount ?? 0),
      }}
      controlledState={{
        selection: { selectAll: false, toggledNodes: ids },
        columnFilters,
        globalFilter: params.text ?? '',
        pagination: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 100,
        },
        sorting: params.sortAttribute
          ? [
              {
                id: params.sortAttribute,
                desc: params.sortDirection === 'DESC',
              },
            ]
          : [],
      }}
      onSelectionChange={(state: TableState) =>
        onIdsChange(
          nextIds(
            ids,
            state.selection,
            rows.filter((row) => !existing?.has(row.id)).map((row) => row.id)
          )
        )
      }
      scroll={{
        mode: 'paginated',
        serverDriven: true,
        totalCount: result.data?.totalCount ?? 0,
      }}
      onPaginationChange={({ page, pageSize }) => update({ page, pageSize })}
      // As the CRM Tract Library: a cleared sort falls back to newest first.
      onSortChange={({ sorting }) => {
        const [sort] = sorting;
        update(
          sort
            ? {
                sortAttribute: sort.id,
                sortDirection: sort.desc ? 'DESC' : 'ASC',
              }
            : { sortAttribute: 'createdAt', sortDirection: 'DESC' }
        );
      }}
      onGlobalSearchChange={(state) =>
        update({ text: state.globalFilter || undefined })
      }
      onFilterChange={(state) => {
        const next = state.columnFilters as ColumnFiltersState;
        onSearchChange((current) => ({
          columnFilters: next,
          params: {
            ...current.params,
            page: 1,
            ...crmTractColumnFiltersToParams(next),
          },
        }));
      }}
      panelFilters={crmTractPanelFilters(params, update)}
      renderFilterPanel={() => (
        <CrmTractFilters
          facets={result.data?.facets}
          hasVestingDocument={isFilteringToHasDocument(params.vestingStatus)}
          onHasVestingDocumentChange={(checked) =>
            update({
              vestingStatus: checked ? HAS_DOCUMENT_STATUSES : undefined,
            })
          }
          hasExternalTitleSupport={!!params.hasExternalTitleSupport}
          onHasExternalTitleSupportChange={(checked) =>
            update({ hasExternalTitleSupport: checked || undefined })
          }
          portfolioAssets={!!params.portfolioAssets}
          onPortfolioAssetsChange={(checked) =>
            update({ portfolioAssets: checked || undefined })
          }
        />
      )}
    />
  );
};

/** What the tract finder has chosen, held by the caller so a wizard step that remounts keeps it. */
export interface FoundTracts {
  mode: 'tract' | 'document';
  ids: string[];
  /** Every row seen, so the Selected Tracts list can name a pick. */
  rows: Map<string, CrmTractTableRow>;
  /** The ids of every document a picked tract was found through, first first. */
  via: Map<string, number[]>;
  documents: DocumentResponse[];
  vestingOnly: boolean;
  search: TractSearch;
}
export const EMPTY_FOUND: FoundTracts = {
  mode: 'tract',
  ids: [],
  rows: new Map(),
  via: new Map(),
  documents: [],
  vestingOnly: false,
  search: EMPTY_TRACT_SEARCH,
};

const documentLabel = (doc: DocumentResponse) =>
  doc.documentTitle || `Document ${doc.id}`;

const viaLabel = (found: FoundTracts, id: string) => {
  const [first] = found.via.get(id) ?? [];
  const doc = found.documents.find((d) => d.id === first);
  return doc && documentLabel(doc);
};

// Notes the document against each of these tracts that is picked and not yet noted.
const noteVia = (
  current: FoundTracts,
  tractIds: string[],
  docId: number
): FoundTracts => {
  const fresh = tractIds.filter(
    (id) => current.ids.includes(id) && !current.via.get(id)?.includes(docId)
  );
  if (!fresh.length) return current;
  const via = new Map(current.via);
  fresh.forEach((id) => via.set(id, [...(via.get(id) ?? []), docId]));
  return { ...current, via };
};

// Every change to the picks: `via` names only picked tracts, and a new pick through a document notes it.
const withIds = (
  current: FoundTracts,
  ids: string[],
  viaId?: number
): FoundTracts => {
  const via = new Map([...current.via].filter(([id]) => ids.includes(id)));
  const next = { ...current, ids, via };
  return viaId === undefined
    ? next
    : noteVia(next, difference(ids, current.ids), viaId);
};

// The rows seen so far, these added; unchanged when it already holds them.
export const withRows = (
  current: FoundTracts,
  rows: CrmTractTableRow[]
): FoundTracts => {
  if (rows.every((row) => current.rows.get(row.id) === row)) return current;
  const known = new Map(current.rows);
  rows.forEach((row) => known.set(row.id, row));
  return { ...current, rows: known };
};

export const OpenDraftsWarning: FC<{
  tracts: { id: string; name: string }[];
  workspaceId?: string;
}> = ({ tracts, workspaceId }) => {
  const ids = tracts.map((tract) => tract.id);
  const { data } = useOpenDrafts(ids, workspaceId);
  const drafts = ids.length ? (data?.openDrafts ?? []) : [];
  const lines = tracts.flatMap(({ id, name }) => {
    const names = drafts
      .filter((draft) => draft.salesforceTractIds.includes(id))
      .map((draft) => draft.name);
    return names.length ? [`${name} is also in ${names.join(', ')}`] : [];
  });
  if (!lines.length) return null;
  return (
    <InlineNotification
      kind="warning"
      lowContrast
      hideCloseButton
      title="Tracts in other draft workspaces"
      subtitle={`${lines.join(
        '; '
      )}. Whichever promotes second must redo any change the first one made.`}
    />
  );
};

const VestedTracts: FC<{
  doc: DocumentResponse;
  found: FoundTracts;
  onChange: Dispatch<SetStateAction<FoundTracts>>;
  remember: (rows: CrmTractTableRow[]) => void;
  existing?: Set<string>;
}> = ({ doc, found, onChange, remember, existing }) => {
  const [search, setSearch] = useState<TractSearch>(() => ({
    ...EMPTY_TRACT_SEARCH,
    params: { ...EMPTY_TRACT_SEARCH.params, documentId: doc.id },
  }));
  // A tract already picked is found through this document too once its table lists it.
  const onRows = useCallback(
    (rows: CrmTractTableRow[]) => {
      remember(rows);
      onChange((current) =>
        noteVia(
          current,
          rows.map((row) => row.id),
          doc.id
        )
      );
    },
    [remember, onChange, doc.id]
  );
  return (
    <>
      <h4 className={styles.tableHeader}>Tracts for {documentLabel(doc)}</h4>
      <TractPicker
        ids={found.ids}
        onIdsChange={(ids) =>
          onChange((current) => withIds(current, ids, doc.id))
        }
        onRowsChange={onRows}
        search={search}
        onSearchChange={setSearch}
        existing={existing}
      />
    </>
  );
};

export const TractFinder: FC<{
  found: FoundTracts;
  onChange: Dispatch<SetStateAction<FoundTracts>>;
  /** The workspace adding them, left out of the open-drafts warning. */
  workspaceId?: string;
  /** The workspace's tracts, which cannot be picked again. */
  existing?: Set<string>;
}> = ({ found, onChange, workspaceId, existing }) => {
  const remember = useCallback(
    (rows: CrmTractTableRow[]) =>
      onChange((current) => withRows(current, rows)),
    [onChange]
  );
  const usedAtom = useMemo(
    () => atom(found.documents.map((doc) => doc.id)),
    [found.documents]
  );
  const drop = (id: string) =>
    onChange((current) =>
      withIds(
        current,
        current.ids.filter((other) => other !== id)
      )
    );

  return (
    <div className={styles.picker}>
      <RadioButtonGroup
        legendText="Find Tracts By"
        name="tsw-find-tracts-by"
        orientation="horizontal"
        valueSelected={found.mode}
        onChange={(mode) =>
          onChange((current) => ({
            ...current,
            mode: mode as FoundTracts['mode'],
          }))
        }
      >
        <RadioButton id="tsw-find-by-tract" labelText="Tract" value="tract" />
        <RadioButton
          id="tsw-find-by-document"
          labelText="Document"
          value="document"
        />
      </RadioButtonGroup>
      {found.mode === 'tract' ? (
        <div className={styles.pickerTable}>
          <TractPicker
            ids={found.ids}
            onIdsChange={(ids) => onChange((current) => withIds(current, ids))}
            onRowsChange={remember}
            search={found.search}
            onSearchChange={(update) =>
              onChange((current) => ({
                ...current,
                search:
                  typeof update === 'function'
                    ? update(current.search)
                    : update,
              }))
            }
            existing={existing}
          />
        </div>
      ) : (
        <>
          <div className={styles.chosen}>
            <span className={styles.intro}>
              Choose a document, then check its tracts to add them: the tracts
              it vests, and the tracts in its survey areas.
            </span>
            {found.documents.map((doc) => (
              <DismissibleTag
                key={doc.id}
                type="gray"
                text={documentLabel(doc)}
                title={`Remove ${documentLabel(doc)}`}
                dismissTooltipLabel={`Remove ${documentLabel(doc)}`}
                // Its tracts stay picked, no longer noted as found through it.
                onClose={() =>
                  onChange((current) => ({
                    ...current,
                    documents: current.documents.filter((d) => d.id !== doc.id),
                    via: new Map(
                      [...current.via].flatMap(([id, docIds]) => {
                        const left = docIds.filter((other) => other !== doc.id);
                        return left.length ? [[id, left] as const] : [];
                      })
                    ),
                  }))
                }
              />
            ))}
          </div>
          <Checkbox
            id="tsw-vesting-documents-only"
            labelText="Vesting Documents Only"
            checked={found.vestingOnly}
            onChange={(_, { checked }) =>
              onChange((current) => ({ ...current, vestingOnly: checked }))
            }
          />
          <DocumentsSearch
            onAddDocument={(doc) =>
              onChange((current) => {
                if (current.documents.some((d) => d.id === doc.id))
                  return current;
                return { ...current, documents: [...current.documents, doc] };
              })
            }
            usedDocumentsAtom={usedAtom}
            noGutter
            hideMap
            vestingOnly={found.vestingOnly}
          />
          {found.documents.map((doc) => (
            <VestedTracts
              key={doc.id}
              doc={doc}
              found={found}
              onChange={onChange}
              remember={remember}
              existing={existing}
            />
          ))}
        </>
      )}
      <h4 className={styles.tableHeader}>
        Selected Tracts (<FormattedCount value={found.ids.length} />)
      </h4>
      <div className={styles.chosen}>
        {found.ids.length ? (
          found.ids.map((id) => {
            const via = viaLabel(found, id);
            const label = [found.rows.get(id)?.name ?? id, via && `via ${via}`]
              .filter(Boolean)
              .join(' · ');
            return (
              <DismissibleTag
                key={id}
                type="gray"
                text={label}
                title={`Remove ${label}`}
                dismissTooltipLabel={`Remove ${label}`}
                onClose={() => drop(id)}
              />
            );
          })
        ) : (
          <span>No tracts selected</span>
        )}
      </div>
      <OpenDraftsWarning
        tracts={found.ids.map((id) => ({
          id,
          name: found.rows.get(id)?.name ?? id,
        }))}
        workspaceId={workspaceId}
      />
    </div>
  );
};

const REVIEW_TABLE = {
  ...STATIC_TABLE_CONFIG,
  sizeToContent: true,
  export: true,
};

// Fetched on expand, so Review loads only the tracts someone opens.
const ReviewVesting: FC<{ salesforceTractId: string }> = ({
  salesforceTractId,
}) => {
  const { data, error, isPending } =
    useTractVestingDocuments(salesforceTractId);
  if (isPending) return <InlineLoading description="Loading…" />;
  if (error)
    return (
      <WorkspaceCardNote>
        Could not load the vesting documents.
      </WorkspaceCardNote>
    );
  const rows = data?.vestingDocuments ?? [];
  return (
    <div className={styles.expanded}>
      {rows.length ? (
        rows.map((row) => (
          <VestingDocumentDetail
            key={row.id}
            document={{ details: { ...row } }}
            readOnly
          />
        ))
      ) : (
        <WorkspaceCardNote>No vesting document yet.</WorkspaceCardNote>
      )}
    </div>
  );
};

// The tracts about to be added, each expandable to its vesting documents.
export const TractsReview: FC<{ found: FoundTracts }> = ({ found }) => {
  const columns = useMemo(() => {
    const base = tractColumns();
    if (!found.ids.some((id) => found.via.has(id))) return base;
    const via: DataTableColumnDef<CrmTractTableRow> = {
      id: 'via',
      header: 'Found Via',
      size: 280,
      accessorFn: (row) => viaLabel(found, row.id) ?? EMPTY,
      meta: { type: 'text' },
    };
    const at = base.findIndex((column) => column.id === 'name') + 1;
    return [...base.slice(0, at), via, ...base.slice(at)];
  }, [found]);
  const rows = useMemo(
    () => found.ids.flatMap((id) => found.rows.get(id) ?? []),
    [found.ids, found.rows]
  );
  return (
    <>
      <h3 className={styles.tableHeader}>
        Tracts (<FormattedCount value={found.ids.length} />)
      </h3>
      <DataTable<CrmTractTableRow>
        data={rows}
        columns={columns}
        getRowId={(row) => row.id}
        config={REVIEW_TABLE}
        renderRowSubComponent={(row) => (
          <ReviewVesting salesforceTractId={row.original.id} />
        )}
      />
    </>
  );
};
